import type { AppConfig, FeatureName, Features, PackageSettings, ServerConfig } from '@ms-cloudpack/common-types';
import { allFeatures } from '@ms-cloudpack/common-types';
import type { DefineFlags } from '@ms-cloudpack/common-types-browser';
import { readJson } from '@ms-cloudpack/json-utilities';
import { resolveModule } from '@ms-cloudpack/path-utilities';
import fs from 'fs';
import path from 'path';
import { pathToFileURL } from 'url';
import { getConfigPath } from '../getConfigPath.js';
import { validateMatch } from '../packageSettings/validateMatch.js';
import { expandEnvironmentVariables } from './expandEnvironmentVariables.js';
import { mergeParentConfig } from './mergeParentConfig.js';
import { resolveParentConfig } from './resolveParentConfig.js';
import { augmentAppConfig } from './augmentAppConfig.js';
import { expandModulePaths } from './expandModulePaths.js';
import { processRelayConfig } from './processRelayConfig.js';

export interface ReadAppConfigOptions {
  /**
   * Extra feature settings. There will be a warning if any of these are invalid.
   */
  extraFeatures?: Features;

  /**
   * Extra package settings to add at the end of user-provided settings (so they take precedence).
   */
  extraPackageSettings?: PackageSettings[];

  /**
   * Extra define flags to add at the end of user-provided settings (so they take precedence).
   */
  extraDefineFlags?: DefineFlags;

  /**
   * Enable default features that are not explicitly set in the config.
   */
  enableDefaultFeatures?: boolean;

  /**
   * Overrides for the server config. Undefined keys will be ignored.
   */
  extraServerConfig?: Pick<ServerConfig, 'open' | 'showOverlay'>;

  /**
   * Custom config file name to use instead of cloudpack.config.json
   */
  configFileName?: string;
}

/**
 * Application configuration combined with the file paths that were used to load it.
 */
export interface AppConfigWithPaths {
  appConfig: AppConfig;

  /**
   * The paths to the config files that were read, in order of precedence.
   */
  appConfigPaths: string[];
}

/**
 * Reads the user config file and merges with any parent configs asynchronously.
 * Note this is only useful for making modifications to the user config.
 * For a full merged representation of config, use `readConfig` instead.
 *
 * Throws an error if the config file exists but is not valid JSON, there's some error reading it,
 * or any settings are invalid. (As of writing, this doesn't do full schema validation; it only
 * checks certain specific settings.)
 */
export async function readAppConfig(appPath: string, options?: ReadAppConfigOptions): Promise<AppConfigWithPaths> {
  const { appConfigPaths } = getConfigPath(appPath, options?.configFileName);

  let config: AppConfigWithPaths;

  // Check for JS and JSON config files.
  const existingPaths = appConfigPaths.filter((p) => fs.existsSync(p));
  if (existingPaths.length > 1) {
    const message = 'Multiple Cloudpack config files found. Determine the correct one and delete the others:';
    throw new Error([message, ...existingPaths].join('\n  '));
  }

  const appConfigPath = existingPaths[0];
  if (!appConfigPath) {
    // If a custom config was specified, it must exist
    if (options?.configFileName) {
      throw new Error(`Config file not found: ${options.configFileName}`);
    }
    // This is fine, use an empty config
    config = { appConfig: {}, appConfigPaths: [] };
  } else if (path.extname(appConfigPath) === '.json') {
    // Check for an existing JSON app config first
    config = await readJsonAppConfig(appConfigPath);
  } else {
    // Otherwise, read as a JS file
    config = await readJsAppConfig(appConfigPath);
  }

  expandEnvironmentVariables(config.appConfig as Record<string, unknown>);

  // Merge extra settings (usually from CLI options) into the final config.
  if (options) {
    config.appConfig = augmentAppConfig(config.appConfig, options);
  }

  if (appConfigPath) {
    if (config.appConfigPaths) {
      config.appConfigPaths?.unshift(appConfigPath); // Add the config path to the beginning of the list
    } else {
      config.appConfigPaths = [appConfigPath]; // If no paths were set, just use the current one
    }
  }

  return config;
}

/**
 * Resolve import specifiers for bundler capabilities registry.
 * Throws if a specifier can't be resolved.
 */
function resolveBundlerCapabilities(params: { appConfig: AppConfig; configPath: string }): void {
  const {
    appConfig: { bundlerCapabilitiesRegistry },
    configPath,
  } = params;
  if (!bundlerCapabilitiesRegistry) {
    return;
  }

  for (const [name, importSpecifier] of Object.entries(bundlerCapabilitiesRegistry)) {
    try {
      const moduleUrl = resolveModule({ parentPath: configPath, importSpecifier });
      bundlerCapabilitiesRegistry[name] = moduleUrl.toString();
    } catch (e) {
      throw new Error(`Error resolving bundler capability  "${importSpecifier}":\n${(e as Error).message || e}`, {
        cause: e,
      });
    }
  }
}

/**
 * Reads the JSON app config file and calls `processConfig` on it.
 * Throws an error if the config file exists but is not valid JSON, or there's some error reading it.
 * @param configPath - The path to the config file.
 * @returns The parsed AppConfig object.
 */
async function readJsonAppConfig(configPath: string): Promise<AppConfigWithPaths> {
  const appConfig = await readJson<AppConfig>(configPath, { mode: 'permissive', throwOnError: true });
  if (!appConfig) {
    // shouldn't happen, but just in case/to satisfy the types...
    throw new Error(`Cloudpack config not found at ${configPath}`);
  }
  return processConfig(configPath, appConfig);
}

/**
 * Reads the JavaScript app config file and calls `processConfig` on it.
 * Throws an error if the config file exists but is not valid, or there's some error reading it.
 * @param configPath - The path to the config file.
 * @returns The parsed AppConfig object.
 */
async function readJsAppConfig(configPath: string): Promise<AppConfigWithPaths> {
  // Convert configPath to a URL for import.
  try {
    const appConfig = (await (import(pathToFileURL(configPath).toString()) as Promise<{ default?: AppConfig }>))
      .default;
    if (!appConfig) {
      throw new Error(`Missing default export from Cloudpack config at ${configPath}`);
    }
    return processConfig(configPath, appConfig);
  } catch (e) {
    throw new Error(`Error reading config file ${configPath}:\n${(e as Error).message || e}`, { cause: e });
  }
}

/**
 * Processes the app config file and merges it with any parent configs.
 * This includes validating the config, checking for deprecated values, and resolving bundler capabilities.
 */
async function processConfig(configPath: string, appConfig: AppConfig): Promise<AppConfigWithPaths> {
  if (!validateConfig(appConfig)) {
    throw new Error(`Config file ${configPath} has invalid settings (see above for details)`);
  }

  checkFeatures(appConfig.features, configPath);

  resolveBundlerCapabilities({ appConfig, configPath });

  expandModulePaths({ appConfig, configPath });

  processRelayConfig({ appConfig, configPath });

  processDeprecatedValues(appConfig);

  const configPaths: string[] = [];

  // Read and merge with any parent configs (the same processing is applied to parent configs)
  const { extends: _extends = [] } = appConfig;
  const extendsArray = Array.isArray(_extends) ? _extends : [_extends];
  const parentResults = await Promise.all(
    extendsArray.map(async (importSpecifier) => {
      const parentConfigPath = resolveParentConfig({ configPath, importSpecifier });

      const result =
        path.extname(parentConfigPath).toLowerCase() === '.json'
          ? await readJsonAppConfig(parentConfigPath)
          : await readJsAppConfig(parentConfigPath);

      // Collect paths from parent configs
      configPaths.push(...result.appConfigPaths);
      return result.appConfig;
    }),
  );

  const mergedConfig = mergeParentConfig({ appConfig, parentConfigs: parentResults });
  return { appConfig: mergedConfig, appConfigPaths: configPaths };
}

/**
 * Validate parts of the current config file (before merging with parents) and log any errors.
 * Logging here and then letting the caller throw if needed allows us to show more errors at once.
 * @returns true if valid, false if not
 */
function validateConfig(appConfig: AppConfig): boolean {
  let isValid = true;

  // Verify that there are no matches with a negated name and a version
  for (const setting of appConfig.packageSettings || []) {
    const { match } = setting;
    const matches = Array.isArray(match) ? match : [match];
    for (const m of matches) {
      const result = validateMatch(m);
      if (!result.isValid) {
        isValid = false;
        console.error(`Invalid packageSettings match: ${result.error}`);
      }
    }
  }

  return isValid;
}

/**
 * Warn if any feature names are invalid, and remove them.
 */
function checkFeatures(features: Record<string, boolean> | undefined, configPath: string): void {
  if (!features) {
    return;
  }

  const invalidFeatures: string[] = [];
  for (const featureName of Object.keys(features)) {
    if (!allFeatures[featureName as FeatureName]) {
      invalidFeatures.push(featureName);
      delete features[featureName];
    }
  }

  if (invalidFeatures.length) {
    console.warn(
      `Unknown feature(s) found in ${configPath} (most likely they were graduated or removed in Cloudpack):\n  ` +
        invalidFeatures.join(', '),
    );
  }
}

/**
 * Delete deprecated values and convert them to the new format. (This mutates the `config` object.)
 */
function processDeprecatedValues(config: AppConfig): void {
  /* eslint-disable etc/no-deprecated */
  const { devServer, server, packageSettings = [] } = config;

  if (devServer) {
    if (server) {
      throw new Error('Cannot have both "devServer" and "server" in the config file. Use "server" only.');
    }

    config.server = devServer;
    delete config.devServer;
  }

  if (server?.routes) {
    config.routes = server.routes;
    delete server.routes;
  }

  for (const setting of packageSettings) {
    if (setting.bundlerType) {
      setting.bundler = setting.bundlerType;
      delete setting.bundlerType;
    }
  }
  /* eslint-enable etc/no-deprecated */
}
